<template>
    <div class="public-client-spouse-step" data-cy="public-client-spouse-step">
        <div class="spouse-hero">
            <div class="spouse-hero__content">
                <span class="spouse-hero__eyebrow">Etapa opcional</span>
                <h4 class="spouse-hero__title">Deseja informar os dados do cônjuge?</h4>
                <p class="spouse-hero__text">
                    Localize um cônjuge ja cadastrado pelo CPF ou preencha os dados pessoais para criar um novo
                    cadastro.
                </p>
            </div>

            <div class="spouse-hero__status">
                <span class="spouse-hero__status-icon">
                    <i class="icon-people"></i>
                </span>
                <small>Status da etapa</small>
                <strong>{{ spouseStatusLabel }}</strong>
            </div>
        </div>

        <div class="spouse-choice-card">
            <button
                    type="button"
                    class="spouse-choice-card__option"
                    :class="{'spouse-choice-card__option--active': spouse.fillMode === 'search'}"
                    data-cy="public-client-spouse-search-mode"
                    @click="changeFillMode('search', true)"
            >
                <span><i class="icon-search"></i></span>
                <strong>Localizar por CPF</strong>
                <small>Use quando o cônjuge ja possui cadastro no sistema.</small>
            </button>

            <button
                    type="button"
                    class="spouse-choice-card__option"
                    :class="{'spouse-choice-card__option--active': spouse.fillMode === 'manual'}"
                    data-cy="public-client-spouse-manual-mode"
                    @click="changeFillMode('manual', true)"
            >
                <span><i class="icon-person_add"></i></span>
                <strong>Cadastrar cônjuge</strong>
                <small>Informe os dados pessoais do cônjuge manualmente.</small>
            </button>
        </div>

        <transition name="spouse-fade" mode="out-in">
            <div v-if="spouse.fillMode === 'skip'" key="skip" class="spouse-empty-card">
                <span class="spouse-empty-card__icon"><i class="icon-check_circle"></i></span>
                <div>
                    <h5>Nenhum cônjuge informado</h5>
                    <p>
                        Esta etapa e opcional. Voce pode seguir o cadastro e voltar aqui caso precise vincular alguem.
                    </p>
                </div>
            </div>

            <div v-else-if="spouse.fillMode === 'search'" key="search">
                <div class="spouse-search-card">
                    <div class="spouse-search-card__header">
                        <div>
                            <span class="spouse-form-card__eyebrow">Busca de cadastro</span>
                            <h5>Localizar cônjuge</h5>
                        </div>
                        <span class="spouse-form-card__badge">CPF obrigatorio</span>
                    </div>

                    <div class="form-group mb-0">
                        <label for="spouse_taxpayer" class="required">CPF do cônjuge</label>
                        <div class="spouse-input">
                            <i class="icon-person spouse-input__icon"></i>
                            <input
                                    id="spouse_taxpayer"
                                    v-model="spouse.taxpayer"
                                    type="text"
                                    inputmode="numeric"
                                    maxlength="14"
                                    v-mask="['###.###.###-##']"
                                    class="form-control spouse-input__control"
                                    placeholder="000.000.000-00"
                                    data-cy="public-client-spouse-taxpayer"
                                    @input="formatCpf"
                                    @change="changeTaxpayer()"
                            >
                        </div>
                        <div style="color: red" v-if="invalid.taxpayer">{{ invalid.taxpayer }}</div>
                        <small class="form-text text-muted">
                            Digite um cpf para buscar. </small>
                    </div>

                    <div class="spouse-search-card__actions">
                        <button type="button" @click="changeTaxpayer()" class="btn btn-primary" data-cy="public-client-spouse-search">
                            <i class="icon-search"></i>
                            Buscar cônjuge
                        </button>
                        <button type="button" class="btn btn-outline-secondary" @click="changeFillMode('manual', true)">
                            Cadastrar novo
                        </button>
                    </div>

                    <div class="spouse-found-card" v-if="spouse.id">
                        <div class="spouse-found-card__header">
                            <span class="spouse-found-card__icon"><i class="icon-person"></i></span>
                            <div>
                                <small>Cadastro encontrado</small>
                                <h5>{{ spouse.name ? spouse.name : 'Nome nao informado' }}</h5>
                            </div>
                            <span class="spouse-found-card__badge">Somente leitura</span>
                        </div>

                        <div class="spouse-found-card__grid">
                            <div class="spouse-found-card__field">
                                <small>CPF</small>
                                <strong>{{ spouse.taxpayer || 'Nao informado' }}</strong>
                            </div>
                            <div class="spouse-found-card__field">
                                <small>E-mail</small>
                                <strong>{{ spouse.email || 'Nao informado' }}</strong>
                            </div>
                            <div class="spouse-found-card__field">
                                <small>Telefone</small>
                                <strong>{{ spouse?.phones[0]?.number || 'Nao informado' }}</strong>
                            </div>
                            <div class="spouse-found-card__field">
                                <small>Data de nascimento</small>
                                <strong>{{ spouse.birthday || 'Nao informado' }}</strong>
                            </div>
                            <div class="spouse-found-card__field">
                                <small>RG</small>
                                <strong>{{ spouse.rg || 'Nao informado' }}</strong>
                            </div>
                            <div class="spouse-found-card__field">
                                <small>Profissão</small>
                                <strong>{{ spouse?.professions?.termo || 'Nao informado' }}</strong>
                            </div>
                        </div>

                        <div class="spouse-found-card__notice">
                            <i class="icon-lock"></i>
                            <span>Dados carregados do cadastro existente. Para alterar, acesse o cadastro do cliente encontrado.</span>
                        </div>
                    </div>
                </div>
            </div>

            <div v-else key="manual" class="spouse-form-card">
                <div class="spouse-form-card__header">
                    <div>
                        <span class="spouse-form-card__eyebrow">Cadastro manual</span>
                        <h5>Dados pessoais do cônjuge</h5>
                    </div>
                    <span class="spouse-form-card__badge">Novo cônjuge</span>
                </div>
                <div class="row gutters">
                    <div class="col-lg-6 col-md-6">
                        <div class="form-group">
                            <label for="spouse_document" class="required">CPF</label>
                            <input id="spouse_document" @change="changeTaxpayer()" v-model="spouse.taxpayer"
                                   v-mask="['###.###.###-##']" type="text" class="form-control">
                            <div style="color: red" v-if="invalid.taxpayer">{{ invalid.taxpayer }}</div>
                        </div>
                    </div>
                    <div class="col-lg-6 col-md-6">
                        <div class="form-group">
                            <label for="spouse_name" class="required">Nome completo</label>
                            <input id="spouse_name" :disabled="!spouse.taxpayer" v-model="spouse.name" type="text" class="form-control">
                            <div style="color: red" v-if="invalid.name"> {{ invalid.name }}</div>
                        </div>
                    </div>
                    <div class="col-lg-6 col-md-6">
                        <div class="form-group mb-0">
                            <label for="email"
                                   :class="{'required':requiredClassValidation('email', 'natural')}">E-mail</label>
                            <input id="email" :disabled="!spouse.taxpayer" v-model="spouse.email" type="text" class="form-control">
                            <div style="color: red" v-if="invalid.email"> {{ invalid.email }}</div>
                        </div>
                    </div>
                    <div class="col-lg-6 col-md-6">
                        <div class="form-group">
                            <label for="spouse_birthday" :class="{'required':requiredClassValidation('birthday', 'natural')}">Data de nascimento</label>
                            <input id="spouse_birthday" :disabled="!spouse.taxpayer" v-model="spouse.birthday" type="date" class="form-control">
                            <div style="color: red" v-if="invalid.birthday"> {{ invalid.birthday }}</div>
                        </div>
                    </div>
                    <div class="col-lg-4 col-md-6">
                        <div class="form-group">
                            <label for="rg" :class="{'required':requiredClassValidation('rg', 'natural')}">RG</label>
                            <input id="rg" :disabled="!spouse.taxpayer" v-model="spouse.rg" type="text" class="form-control">
                            <div style="color: red" v-if="invalid.rg"> {{ invalid.rg }}</div>
                        </div>
                    </div>
                    <div class="col-lg-4 col-md-6">
                        <div class="form-group">
                            <label for="dispatch_date"
                                   :class="{'required':requiredClassValidation('dispatch_date', 'natural')}">{{
                                    t('CLIENTS.DISPATCH_DATE')
                                }}</label>
                            <input id="dispatch_date" :disabled="!spouse.taxpayer" v-model="spouse.dispatch_date" type="date"
                                   class="form-control">
                            <div style="color: red" v-if="invalid.dispatch_date"> {{ invalid.dispatch_date }}</div>
                        </div>
                    </div>
                    <div class="col-lg-4 col-md-6">
                        <div class="form-group">
                            <label for="dispatching_agency"
                                   :class="{'required':requiredClassValidation('dispatching_agency', 'natural')}">{{
                                    t('CLIENTS.DISPATCH_AGENCY')
                                }}</label>
                            <input id="dispatching_agency" :disabled="!spouse.taxpayer" v-model="spouse.dispatching_agency" type="text"
                                   class="form-control">
                            <div style="color: red" v-if="invalid.dispatching_agency">
                                {{ invalid.dispatching_agency }}
                            </div>
                        </div>
                    </div>
                    <div class="col-lg-4 col-md-6">
                        <div class="form-group mb-lg-0">
                            <label for="sex"
                                   :class="{'required':requiredClassValidation('sex', 'natural')}">Gênero</label>
                            <Select2
                                    id="sex"
                                    v-model="spouse.sex"
                                    :settings="select2Settings"
                                    :options="optionsSex"
                                    :disabled="!spouse.taxpayer"
                                    placeholder="Selecione"
                            />
                            <div style="color: red" v-if="invalid.sex"> {{ invalid.sex }}</div>
                        </div>
                    </div>
                    <div class="col-lg-4 col-md-6">
                        <div class="form-group">
                            <label for="profession_id"
                                   :class="{'required':requiredClassValidation('profession_id', 'natural')}">{{
                                    t('CLIENTS.PROFESSION')
                                }}</label>
                            <Select2
                                    id="profession_id"
                                    :disabled="!spouse.taxpayer"
                                    v-model="spouse.profession_id"
                                    :settings="professionOptions"
                                    placeholder="Selecione"
                            />
                            <div style="color: red" v-if="invalid.profession_id"> {{ invalid.profession_id }}</div>
                        </div>
                    </div>
                    <div class="col-lg-4 col-md-6">
                        <div class="form-group">
                            <label for="work_on_company"
                                   :class="{'required':requiredClassValidation('work_on_company', 'natural')}">Empresa
                                em que
                                trabalha</label>
                            <input id="work_on_company" :disabled="!spouse.taxpayer" v-model="spouse.work_on_company" type="text"
                                   class="form-control">
                            <div style="color: red" v-if="invalid.work_on_company"> {{ invalid.work_on_company }}
                            </div>
                        </div>
                    </div>
                    <div class="col-lg-4 col-md-6">
                        <div class="form-group">
                            <label for="income"
                                   :class="{'required':requiredClassValidation('income', 'natural')}">Renda</label>
                            <input id="income" :disabled="!spouse.taxpayer" v-model="spouse.income" type="number" class="form-control">
                            <div style="color: red" v-if="invalid.income"> {{ invalid.income }}</div>
                        </div>
                    </div>
                    <div class="col-lg-4 col-md-6">
                        <div class="form-group">
                            <label for="income_family"
                                   :class="{'required':requiredClassValidation('income_family', 'natural')}">Renda
                                Familiar</label>
                            <input id="income_family" :disabled="!spouse.taxpayer" v-model="spouse.income_family" type="number"
                                   class="form-control">
                            <div style="color: red" v-if="invalid.income_family"> {{ invalid.income_family }}</div>
                        </div>
                    </div>
                    <div class="col-lg-4 col-md-6">
                        <div class="form-group mb-0">
                            <label for="nationality"
                                   :class="{'required':requiredClassValidation('nationality', 'natural')}">{{
                                    t('CLIENTS.NATIONALITY')
                                }}</label>
                            <input id="nationality" :disabled="!spouse.taxpayer" v-model="spouse.nationality" type="text"
                                   class="form-control">
                            <div style="color: red" v-if="invalid.nationality"> {{ invalid.nationality }}</div>
                        </div>
                    </div>
                    <div class="col-lg-4 col-md-6">
                        <div class="form-group mb-0">
                            <label for="naturalness"
                                   :class="{'required':requiredClassValidation('naturalness', 'natural')}">{{
                                    t('CLIENTS.BIRTH_PLACE')
                                }}</label>
                            <input id="naturalness" :disabled="!spouse.taxpayer" v-model="spouse.naturalness" type="text"
                                   class="form-control">
                            <div style="color: red" v-if="invalid.naturalness"> {{ invalid.naturalness }}</div>
                        </div>
                    </div>
                    <div class="col-lg-4 col-md-6">
                        <div class="form-group mb-0">
                            <label for="mother_name"
                                   :class="{'required':requiredClassValidation('mother_name', 'natural')}">{{
                                    t('CLIENTS.MOTHER_NAME')
                                }}</label>
                            <input id="mother_name" :disabled="!spouse.taxpayer" v-model="spouse.mother_name" type="text"
                                   class="form-control">
                            <div style="color: red" v-if="invalid.mother_name"> {{ invalid.mother_name }}</div>
                        </div>
                    </div>
                    <div class="col-lg-4 col-md-6">
                        <div class="form-group mb-0">
                            <label for="father_name"
                                   :class="{'required':requiredClassValidation('father_name', 'natural')}">{{
                                    t('CLIENTS.FATHER_NAME')
                                }}</label>
                            <input id="father_name" :disabled="!spouse.taxpayer" v-model="spouse.father_name" type="text"
                                   class="form-control">
                            <div style="color: red" v-if="invalid.father_name"> {{ invalid.father_name }}</div>
                        </div>
                    </div>
                    <div class="col-lg-12 col-md-12">
                        <div class="form-group mb-0">
                            <label for="description"
                                   :class="{'required':requiredClassValidation('description', 'natural')}">Observação</label>
                            <textarea id="description" :disabled="!spouse.taxpayer" v-model="spouse.description" type="text"
                                      class="form-control">
                            </textarea>
                            <div style="color: red" v-if="invalid.description"> {{ invalid.description }}</div>
                        </div>
                    </div>
                    <div class="col-12">
                        <div class="phone-panel">
                            <div class="phone-panel__header">
                                <span class="phone-panel__icon"><i class="icon-phone"></i></span>
                                <div>
                                    <strong :class="{'required':requiredClassValidation('phones', 'natural')}">Telefones</strong>
                                    <small>Adicione uma ou mais formas de contato.</small>
                                </div>
                            </div>

                            <div class="row gutters align-items-end">
                                <div class="col-lg-3 col-md-6">
                                    <div class="form-group">
                                        <label for="phone_type">Tipo</label>
                                        <Select2
                                                id="phone_type"
                                                :disabled="!spouse.taxpayer"
                                                v-model="phoneDraft.type"
                                                :settings="select2Settings"
                                                :options="phoneTypeOptions"
                                                placeholder="Selecione"
                                        />
                                    </div>
                                </div>
                                <div class="col-lg-3 col-md-6">
                                    <div class="form-group">
                                        <label for="natural_phone_number">Numero</label>
                                        <VueTelInput
                                                id="natural_phone_number"
                                                mode="national"
                                                :disabled="!spouse.taxpayer"
                                                v-model="phoneDraft.number"
                                                :autoFormat="true"
                                                :preferredCountries="['US', 'DE', 'FR', 'CN']"
                                                :defaultCountry="phoneDraft.code_country"
                                                @country-changed="changePhoneCountry"
                                                :dropdownOptions="phoneDropdownOptions"
                                                :inputOptions="phoneInputOptions"
                                        />
                                    </div>
                                </div>
                                <div class="col-lg-4 col-md-8">
                                    <div class="form-group">
                                        <label for="natural_phone_description">Observacao</label>
                                        <input id="natural_phone_description" v-model="phoneDraft.description"
                                               type="text" class="form-control"
                                               :disabled="!spouse.taxpayer"
                                               placeholder="Ex.: WhatsApp, horario comercial...">
                                    </div>
                                </div>
                                <div class="col-lg-2 col-md-4">
                                    <button type="button" :disabled="!spouse.taxpayer" class="btn btn-primary phone-panel__add" @click="addPhone">
                                        <i class="icon-add"></i>
                                        Adicionar
                                    </button>
                                </div>
                            </div>
                            <div style="color: red" v-if="invalid.phones"> {{ invalid.phones }}</div>
                            <div v-if="phones.length" class="phone-list">
                                <div v-for="(phone, index) in phones" :key="index" class="phone-list__item">
                                    <span class="phone-list__icon"><i class="icon-phone"></i></span>
                                    <div class="phone-list__content">
                                        <strong>{{ getPhoneTypeLabel(phone.type) }} · {{ phone.code_country }}
                                            {{ phone.number }}</strong>
                                        <small>{{ phone.description || 'Sem observacao' }}</small>
                                    </div>
                                    <button type="button" class="phone-list__remove" aria-label="Remover telefone"
                                            @click="removePhone(index)">
                                        <i class="icon-trash-2"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import {useToast} from 'vue-toastification';
import {useI18n} from 'vue-i18n';
import {mapActions, mapMutations, mapState} from 'vuex';
import AttributesValidationsMixin from '../../../mixins/AttributesValidationsMixin';
import Select2 from 'vue3-select2-component';
import {VueTelInput} from 'vue-tel-input';

export default {
    name: "createPublicPersonStepConjuge",
    setup() {
        const toast = useToast();
        const {t} = useI18n();
        return {t, toast}
    },
    components: {
        Select2,
        VueTelInput,
    },
    mounted() {
        console.log(this.spouse)
        if (this.spouse?.phones?.length){
            this.phones = this.spouse.phones;
        }
    },
    mixins: [AttributesValidationsMixin],
    data() {
        return {
            fieldsForValidationConjuge: ['description', 'birthday', 'sex', 'rg', 'dispatch_date',
                'dispatching_agency', 'profession_id', 'income', 'income_family', 'work_on_company', 'email',
                'nationality', 'naturalness', 'mother_name', 'father_name'
            ],
            invalid: {},
            select2Settings: {
                width: '100%',
            },
            optionsSex: ['MASCULINO', 'FEMININO', 'OUTROS'],
            professionOptions: {
                width: '100%',
                language: {
                    inputTooShort: function () {
                        return "Por favor, insira ao menos 3 caracteres para pesquisar.";
                    },
                    noResults: function () {
                        return "Nenhum resultado encontrado!";
                    }
                },
                minimumInputLength: 3,
                ajax: {
                    url: `${process.env.VUE_APP_BACK_END}/api/v1/publica/profissoes/listar`,
                    dataType: "json",
                    data: params => {
                        return {
                            term: params.term,
                        };
                    },
                    processResults: function (data) {
                        if (!(data.validations)) {
                            data.unshift({id: 0, text: "Nenhum"});
                            return {
                                results: data.map(({id, text: text}) => ({id, text}))
                            };
                        } else {
                            return {results: ""};
                        }
                    },
                }
            },
            phoneDraft: {
                type: null,
                number: null,
                code_country: 55,
                country_acronym: 'BR',
                description: '',
            },
            phones: [],
            phoneDropdownOptions: {
                showDialCodeInList: true,
                showDialCodeInSelection: true,
                showFlags: true,
                showSearchBox: true,
                tabindex: 3,
            },
            phoneInputOptions: {
                autocomplete: 'on',
                autofocus: true,
                'aria-describedby': 'des',
                showDialCode: false,
                placeholder: 'Numero Telefone',
                tabindex: 3,
                maxlength: 19,
            },
            phoneTypeOptions: [
                {id: 0, text: 'Whatsapp'},
                {id: 1, text: 'Ligação'},
                {id: 2, text: 'Ligação e Whatsapp'}
            ],
        }
    },
    computed: {
        spouseStatusLabel() {
            if (this.spouse.fillMode === 'skip') return 'Nao informado';
            if (this.spouse.fillMode === 'manual') return 'Cadastro manual';
            return 'Busca por CPF';
        },
        ...mapState({
            spouse: (state) => state.publicClient.spouse,
            attrsPhisical: (state) => state.publicClient.attrsPhisical,
        }),
    },
    methods: {
        ...mapActions({findClientPublicAction: 'findClientPublicAction'}),
        ...mapMutations({
            setSpouseFillModePublicClientMutation: 'setSpouseFillModePublicClientMutation',
            setSpousePublicClientMutation: 'setSpousePublicClientMutation',
        }),
        validateConjuge() {
            if (this.spouse.fillMode === 'search' && this.spouse.id){
                this.setSpousePublicClientMutation(this.spouse);
                return true;
            }
            if (!this.spouse.taxpayer){
                this.setSpousePublicClientMutation({fillMode: 'skip'});
                return true;
            }
            if (this.phoneDraft.type && this.phoneDraft.number) {
                this.pushInPhones();
            }
            this.spouse.phones = this.phones;
            this.setVerifyInformedField('name');
            this.setVerifyInformedField('taxpayer');
            if (this.attrsPhisical.find(atr => atr.name === 'phones' && atr.is_required === true)) {
                if (!this.spouse.phones.length) {
                    this.invalid.phones = 'Necessario informar um telefone';
                } else {
                    this.invalid.phones = null;
                }
            }
            this.fieldsForValidationConjuge.forEach(i => {
                if (this.attrsPhisical.find(atr => atr.name === i && atr.is_required === true)) {
                    this.setVerifyInformedField(i);
                }
            })
            let can_continue = true;
            Object.entries(this.invalid).forEach(([index]) => {
                if (this.invalid[index]) {
                    can_continue = false;
                }
            })
            this.setSpousePublicClientMutation(this.spouse);
            return can_continue;
        },
        setVerifyInformedField(index) {
            if (!this.spouse[index]) {
                this.invalid[index] = 'Campo Obrigatório';
            } else {
                this.invalid[index] = null;
            }
        },
        removePhone(index) {
            this.phones.splice(index, 1);
        },
        getPhoneTypeLabel(type) {
            return this.phoneTypeOptions.find(option => option.id === type)?.text || 'Telefone';
        },
        addPhone() {
            if (!this.phoneDraft.type || !this.phoneDraft.number) {
                this.toast.info('Informe o tipo e o numero do telefone.');
                return;
            }
            this.pushInPhones();
        },
        pushInPhones() {
            this.phones.push({
                type: this.phoneDraft.type,
                number: this.phoneDraft.number,
                code_country: this.phoneDraft.code_country,
                country_acronym: this.phoneDraft.country_acronym,
                description: this.phoneDraft.description,
            });
            this.phoneDraft = {
                type: null,
                number: '',
                code_country: 55,
                country_acronym: 'BR',
                description: '',
            };
        },
        changePhoneCountry(country) {
            this.phoneDraft.code_country = country?.dialCode || 55;
            this.phoneDraft.country_acronym = country?.iso2 || 'BR';
        },
        changeFillMode(fillMode, clear) {
            if (clear) {
                this.setSpousePublicClientMutation({fillMode: fillMode});
            } else {
                this.setSpouseFillModePublicClientMutation(fillMode);
            }
        },
        formatCpf() {
            const digits = String(this.spouse.taxpayer || '').replace(/\D/g, '').slice(0, 11);
            this.spouse.taxpayer = digits
                    .replace(/^(\d{3})(\d)/, '$1.$2')
                    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
                    .replace(/\.(\d{3})(\d)/, '.$1-$2');
        },
        async changeTaxpayer() {
            if (this.isValidCpf(this.spouse.taxpayer)) {
                this.invalid.taxpayer = null;
                let tax = btoa(this.spouse.taxpayer);
                await this.findClientPublicAction({
                    taxOrId: tax, companyId: this.$route.params.companyId
                }).then(resp => {
                    if (resp.data?.validations) {
                        const data = {
                            taxpayer: this.spouse.taxpayer,
                            fillMode: 'manual',
                        }
                        this.setSpousePublicClientMutation(data);
                        this.toast.success('Cônjuge não possui cadastro');
                    } else {
                        this.toast.success('Cônjuge encontrado com sucesso!');
                        resp.data.fillMode = 'search';
                        this.setSpousePublicClientMutation(resp.data);
                    }
                }).finally(() => this.$store.commit("changeLoading", false));
            } else {
                this.invalid.taxpayer = 'CPF inválido'
            }
        },
        onlyDigits(value) {
            return String(value || '').replace(/\D/g, '').slice(0, 14);
        },
        isValidCpf(value) {
            const cpf = this.onlyDigits(value);
            if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) return false;
            const calculateDigit = (base) => {
                const sum = base.split('').reduce((total, digit, index) => {
                    return total + Number(digit) * (base.length + 1 - index);
                }, 0);
                const rest = (sum * 10) % 11;
                return rest === 10 ? 0 : rest;
            };
            return calculateDigit(cpf.slice(0, 9)) === Number(cpf[9])
                    && calculateDigit(cpf.slice(0, 10)) === Number(cpf[10]);
        },
    },
}
</script>

<style scoped>
.public-client-spouse-step {
    color: #243b35;
}

.spouse-hero {
    align-items: stretch;
    background: linear-gradient(135deg, #0e5e4e 0%, #16886f 54%, #f2fbf8 54%, #ffffff 100%);
    border-radius: 22px;
    box-shadow: 0 18px 44px rgba(14, 94, 78, .16);
    display: flex;
    gap: 20px;
    justify-content: space-between;
    margin-bottom: 16px;
    overflow: hidden;
    padding: 28px;
}

.spouse-hero__content {
    max-width: 650px;
    position: relative;
    z-index: 1;
}

.spouse-hero__eyebrow,
.spouse-form-card__eyebrow {
    color: #55d5b6;
    display: inline-block;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: .12em;
    margin-bottom: 10px;
    text-transform: uppercase;
}

.spouse-hero__title {
    color: #ffffff;
    font-size: 28px;
    font-weight: 800;
    line-height: 1.2;
    margin-bottom: 10px;
}

.spouse-hero__text {
    color: rgba(255, 255, 255, .86);
    font-size: 15px;
    line-height: 1.6;
    margin: 0;
}

.spouse-hero__status,
.spouse-choice-card,
.spouse-empty-card,
.spouse-search-card,
.spouse-form-card {
    background: #ffffff;
    border: 1px solid #e4eee9;
    border-radius: 20px;
    box-shadow: 0 16px 36px rgba(21, 56, 48, .08);
}

.spouse-hero__status {
    align-items: flex-start;
    align-self: center;
    background: rgba(255, 255, 255, .92);
    border-color: rgba(255, 255, 255, .7);
    display: flex;
    flex-direction: column;
    min-width: 210px;
    padding: 18px;
}

.spouse-hero__status-icon,
.spouse-empty-card__icon,
.spouse-found-card__icon {
    align-items: center;
    background: #e8faf5;
    border-radius: 14px;
    color: #0e5e4e;
    display: flex;
    font-size: 22px;
    height: 46px;
    justify-content: center;
    margin-bottom: 12px;
    width: 46px;
}

.spouse-hero__status small {
    color: #7a8c87;
    font-weight: 700;
    margin-bottom: 4px;
}

.spouse-hero__status strong {
    color: #173b33;
    font-size: 16px;
}

.spouse-choice-card {
    display: grid;
    gap: 14px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    margin-bottom: 16px;
    padding: 18px;
}

.spouse-choice-card__option {
    background: #f8fbfa;
    border: 1px solid #e0ece7;
    border-radius: 16px;
    color: #38544d;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    min-height: 132px;
    padding: 16px;
    text-align: left;
    transition: all .2s ease;
}

.spouse-choice-card__option span {
    align-items: center;
    background: #ffffff;
    border-radius: 12px;
    color: #16886f;
    display: flex;
    font-size: 20px;
    height: 42px;
    justify-content: center;
    margin-bottom: 12px;
    width: 42px;
}

.spouse-choice-card__option strong {
    color: #1b3b34;
    font-size: 15px;
    margin-bottom: 4px;
}

.spouse-choice-card__option small {
    color: #7b908a;
    line-height: 1.35;
}

.spouse-choice-card__option:hover,
.spouse-choice-card__option--active {
    background: #ecfbf7;
    border-color: #26b390;
    box-shadow: 0 10px 24px rgba(22, 136, 111, .14);
    transform: translateY(-1px);
}

.spouse-choice-card__option--muted span {
    color: #71857f;
}

.spouse-empty-card {
    align-items: center;
    display: flex;
    gap: 18px;
    padding: 24px;
}

.spouse-empty-card h5,
.spouse-form-card h5,
.spouse-search-card h5,
.spouse-found-card h5 {
    color: #173b33;
    font-weight: 800;
    margin-bottom: 4px;
}

.spouse-empty-card p {
    color: #71857f;
    margin-bottom: 6px;
}

.spouse-search-card,
.spouse-form-card {
    padding: 24px;
}

.spouse-search-card__header,
.spouse-form-card__header {
    align-items: center;
    border-bottom: 1px solid #e6efeb;
    display: flex;
    justify-content: space-between;
    margin-bottom: 16px;
    padding-bottom: 18px;
}

.spouse-form-card__eyebrow {
    color: #16886f;
    margin-bottom: 6px;
}

.spouse-form-card__badge,
.spouse-found-card__badge {
    background: #e9fbf6;
    border-radius: 999px;
    color: #0e5e4e;
    font-size: 12px;
    font-weight: 800;
    padding: 8px 12px;
}

.spouse-input {
    position: relative;
}

.spouse-input__icon {
    color: #16886f;
    font-size: 18px;
    left: 17px;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1;
}

.spouse-input__control {
    border-color: #d9e8e3;
    border-radius: 15px;
    font-size: 18px;
    font-weight: 700;
    height: 56px;
    letter-spacing: .04em;
    padding-left: 48px;
}

.spouse-search-card__actions {
    display: flex;
    gap: 10px;
    margin-top: 16px;
}

.spouse-search-card__actions .btn {
    border-radius: 12px;
    font-weight: 800;
    min-height: 44px;
}

.spouse-form-card label,
.spouse-search-card label {
    color: #344f48;
    font-weight: 800;
}

.spouse-form-card .form-control {
    border-color: #dce9e4;
    border-radius: 12px;
    min-height: 44px;
}

.spouse-input__control:focus,
.spouse-form-card .form-control:focus {
    border-color: #19a985;
    box-shadow: 0 0 0 .2rem rgba(25, 169, 133, .14);
}

.spouse-form-card :deep(.select2-container) {
    display: block;
    width: 100% !important;
}

.spouse-form-card :deep(.select2-container--default .select2-selection--single) {
    align-items: center;
    background-color: #ffffff;
    border-color: #dce9e4;
    border-radius: 12px !important;
    display: flex;
    height: 44px;
    min-height: 44px;
    overflow: visible;
}

.spouse-form-card :deep(.select2-container--default .select2-selection--single .select2-selection__rendered),
.spouse-form-card :deep(.select2-container--default .select2-selection--single .select2-selection__arrow) {
    border-radius: 12px;
}

.spouse-form-card :deep(.select2-container--default .select2-selection--single .select2-selection__rendered) {
    color: #243b35;
    line-height: 42px;
    padding-left: 14px;
    padding-right: 34px;
    width: 100%;
}

.spouse-form-card :deep(.select2-container--default .select2-selection--single .select2-selection__arrow) {
    height: 42px;
    right: 8px;
}

.spouse-form-card :deep(.select2-container--default.select2-container--focus .select2-selection--single),
.spouse-form-card :deep(.select2-container--default .select2-selection--single:focus) {
    border-color: #19a985;
    box-shadow: 0 0 0 .2rem rgba(25, 169, 133, .14);
    outline: 0;
}

.spouse-form-card :deep(.select2-container--default .select2-selection--single .select2-selection__placeholder) {
    color: #7f918c;
}

.phone-panel {
    background: linear-gradient(180deg, #f8fdfb 0%, #ffffff 100%);
    border: 1px solid #dfede8;
    border-radius: 18px;
    margin-top: 8px;
    padding: 18px;
}

.phone-panel__header {
    align-items: center;
    display: flex;
    gap: 12px;
    margin-bottom: 16px;
}

.phone-panel__icon,
.phone-list__icon {
    align-items: center;
    background: #e9fbf6;
    border-radius: 14px;
    color: #0e5e4e;
    display: flex;
    flex: 0 0 44px;
    font-size: 20px;
    height: 44px;
    justify-content: center;
    width: 44px;
}

.phone-panel__header strong {
    color: #173b33;
    display: block;
    font-size: 15px;
    font-weight: 800;
    margin-bottom: 2px;
}

.phone-panel__header small {
    color: #71857f;
    display: block;
    line-height: 1.4;
}

.phone-panel__add {
    border-radius: 12px;
    font-weight: 800;
    min-height: 44px;
    width: 100%;
}

.phone-panel :deep(.vue-tel-input) {
    border: 1px solid #dce9e4;
    border-radius: 12px;
    min-height: 44px;
    overflow: visible;
}

.phone-panel :deep(.vue-tel-input:focus-within) {
    border-color: #19a985;
    box-shadow: 0 0 0 .2rem rgba(25, 169, 133, .14);
}

.phone-panel :deep(.vti__dropdown) {
    background: #f8fbfa;
    border-radius: 12px 0 0 12px;
}

.phone-panel :deep(.vti__dropdown-list) {
    border-radius: 12px;
    box-shadow: 0 14px 35px rgba(24, 52, 45, .16);
    z-index: 1050;
}

.phone-panel :deep(.vti__input) {
    color: #243b35;
    min-height: 42px;
}

.phone-list {
    display: grid;
    gap: 10px;
    margin-top: 14px;
}

.phone-list__item {
    align-items: center;
    background: #ffffff;
    border: 1px solid #e0ece7;
    border-radius: 16px;
    display: grid;
    gap: 12px;
    grid-template-columns: 44px minmax(0, 1fr) 38px;
    padding: 12px;
}

.phone-list__content strong {
    color: #173b33;
    display: block;
    font-weight: 800;
    margin-bottom: 3px;
}

.phone-list__content small {
    color: #71857f;
}

.phone-list__remove {
    align-items: center;
    background: #ffffff;
    border: 1px solid #e4eee9;
    border-radius: 12px;
    color: #b53f3f;
    cursor: pointer;
    display: flex;
    height: 38px;
    justify-content: center;
    width: 38px;
}

.spouse-found-card {
    background: linear-gradient(180deg, #f8fdfb 0%, #ffffff 100%);
    border: 1px solid #dfede8;
    border-radius: 18px;
    margin-top: 16px;
    padding: 18px;
}

.spouse-found-card__header {
    align-items: center;
    border-bottom: 1px solid #e6efeb;
    display: grid;
    gap: 12px;
    grid-template-columns: 46px minmax(0, 1fr) auto;
    margin-bottom: 16px;
    padding-bottom: 16px;
}

.spouse-found-card__icon {
    margin-bottom: 0;
}

.spouse-found-card__header small,
.spouse-found-card__field small {
    color: #71857f;
    display: block;
    font-size: 12px;
    font-weight: 800;
    margin-bottom: 4px;
}

.spouse-found-card__header h5 {
    color: #173b33;
    font-weight: 800;
    margin-bottom: 0;
}

.spouse-found-card__badge {
    justify-self: end;
}

.spouse-found-card__grid {
    display: grid;
    gap: 12px;
    grid-template-columns: repeat(3, minmax(0, 1fr));
}

.spouse-found-card__field {
    background: #ffffff;
    border: 1px solid #e4eee9;
    border-radius: 14px;
    padding: 12px;
}

.spouse-found-card__field strong {
    color: #243b35;
    display: block;
    font-weight: 800;
    overflow-wrap: anywhere;
}

.spouse-found-card__notice {
    align-items: center;
    background: #eef8f5;
    border-radius: 14px;
    color: #4e6a62;
    display: flex;
    gap: 10px;
    margin-top: 14px;
    padding: 12px 14px;
}

.spouse-fade-enter-active,
.spouse-fade-leave-active {
    transition: opacity .18s ease, transform .18s ease;
}

.spouse-fade-enter-from,
.spouse-fade-leave-to {
    opacity: 0;
    transform: translateY(8px);
}

html.dark-theme .public-client-spouse-step {
    color: var(--dark-text-soft);
}

html.dark-theme .spouse-hero {
    background: linear-gradient(135deg, #064e3b 0%, #0f766e 52%, var(--dark-surface-2) 52%, var(--dark-surface) 100%);
    box-shadow: var(--dark-shadow);
}

html.dark-theme .spouse-hero__status,
html.dark-theme .spouse-choice-card,
html.dark-theme .spouse-empty-card,
html.dark-theme .spouse-search-card,
html.dark-theme .spouse-form-card,
html.dark-theme .spouse-found-card,
html.dark-theme .phone-panel,
html.dark-theme .phone-list__item {
    background: var(--dark-surface) !important;
    border-color: var(--dark-border) !important;
    box-shadow: var(--dark-shadow) !important;
}

html.dark-theme .spouse-hero__status {
    background: rgba(17, 24, 39, .92) !important;
}

html.dark-theme .spouse-choice-card__option,
html.dark-theme .spouse-found-card__field {
    background: var(--dark-surface-2) !important;
    border-color: var(--dark-border) !important;
}

html.dark-theme .spouse-hero__status-icon,
html.dark-theme .spouse-choice-card__option span,
html.dark-theme .spouse-empty-card__icon,
html.dark-theme .spouse-found-card__icon,
html.dark-theme .phone-panel__icon,
html.dark-theme .phone-list__icon {
    background: var(--dark-primary-soft) !important;
    color: var(--dark-primary) !important;
}

html.dark-theme .spouse-choice-card__option:hover,
html.dark-theme .spouse-choice-card__option--active {
    background: var(--dark-primary-soft) !important;
    border-color: var(--dark-primary) !important;
}

html.dark-theme .spouse-hero__status strong,
html.dark-theme .spouse-choice-card__option strong,
html.dark-theme .spouse-empty-card h5,
html.dark-theme .spouse-search-card h5,
html.dark-theme .spouse-form-card h5,
html.dark-theme .spouse-found-card h5,
html.dark-theme .spouse-found-card__field strong,
html.dark-theme .phone-panel__header strong,
html.dark-theme .phone-list__content strong {
    color: var(--dark-text) !important;
}

html.dark-theme .spouse-hero__status small,
html.dark-theme .spouse-choice-card__option small,
html.dark-theme .spouse-empty-card p,
html.dark-theme .spouse-found-card__header small,
html.dark-theme .spouse-found-card__field small,
html.dark-theme .spouse-found-card__notice,
html.dark-theme .phone-panel__header small,
html.dark-theme .phone-list__content small,
html.dark-theme .form-text,
html.dark-theme .text-muted {
    color: var(--dark-text-muted) !important;
}

html.dark-theme .spouse-input__control,
html.dark-theme .spouse-form-card .form-control,
html.dark-theme .phone-panel :deep(.vue-tel-input),
html.dark-theme .spouse-form-card :deep(.select2-container--default .select2-selection--single) {
    background: var(--dark-surface-2) !important;
    border-color: var(--dark-border) !important;
    color: var(--dark-text-soft) !important;
}

html.dark-theme .spouse-form-card label,
html.dark-theme .spouse-search-card label,
html.dark-theme .phone-panel :deep(.vti__input),
html.dark-theme .spouse-form-card :deep(.select2-container--default .select2-selection--single .select2-selection__rendered) {
    color: var(--dark-text-soft) !important;
}

html.dark-theme .spouse-input__control::placeholder,
html.dark-theme .spouse-form-card .form-control::placeholder,
html.dark-theme .phone-panel :deep(.vti__input::placeholder) {
    color: var(--dark-text-muted) !important;
}

html.dark-theme .spouse-form-card__badge,
html.dark-theme .spouse-found-card__badge {
    background: var(--dark-primary-soft) !important;
    color: var(--dark-primary) !important;
}

html.dark-theme .spouse-found-card__notice {
    background: var(--dark-primary-soft) !important;
}

html.dark-theme .phone-list__remove {
    background: var(--dark-surface-2) !important;
    border-color: var(--dark-border) !important;
    color: var(--dark-danger) !important;
}

html.dark-theme .phone-panel :deep(.vti__dropdown),
html.dark-theme .phone-panel :deep(.vti__dropdown-list) {
    background: var(--dark-surface-2) !important;
    border-color: var(--dark-border) !important;
    color: var(--dark-text-soft) !important;
}

@media (max-width: 991.98px) {
    .spouse-hero {
        background: linear-gradient(160deg, #0e5e4e 0%, #16886f 100%);
        flex-direction: column;
    }

    .spouse-hero__status {
        align-self: stretch;
    }

    .spouse-choice-card,
    .spouse-found-card__grid {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 575.98px) {
    .public-client-spouse-step {
        margin-left: -6px;
        margin-right: -6px;
    }

    .spouse-hero,
    .spouse-choice-card,
    .spouse-empty-card,
    .spouse-search-card,
    .spouse-form-card {
        border-radius: 16px;
        padding: 16px 12px;
    }

    .spouse-hero {
        gap: 14px;
        margin-bottom: 12px;
    }

    .spouse-hero__eyebrow,
    .spouse-form-card__eyebrow {
        font-size: 10px;
        margin-bottom: 8px;
    }

    .spouse-hero__title {
        font-size: 21px;
        line-height: 1.22;
        margin-bottom: 8px;
    }

    .spouse-hero__text {
        font-size: 13px;
        line-height: 1.45;
    }

    .spouse-hero__status {
        align-items: center;
        border-radius: 14px;
        display: grid;
        gap: 4px 10px;
        grid-template-columns: 40px minmax(0, 1fr);
        min-width: 0;
        padding: 12px;
    }

    .spouse-hero__status-icon {
        grid-row: span 2;
        height: 40px;
        margin-bottom: 0;
        width: 40px;
    }

    .spouse-hero__status small,
    .spouse-hero__status strong {
        margin-bottom: 0;
    }

    .spouse-choice-card {
        gap: 8px;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        margin-bottom: 12px;
    }

    .spouse-choice-card__option {
        align-items: center;
        border-radius: 14px;
        min-height: auto;
        padding: 12px 8px;
        text-align: center;
    }

    .spouse-choice-card__option span {
        height: 36px;
        margin-bottom: 8px;
        width: 36px;
    }

    .spouse-choice-card__option strong {
        font-size: 13px;
        line-height: 1.2;
    }

    .spouse-choice-card__option small {
        display: none;
    }

    .spouse-empty-card,
    .spouse-search-card__header,
    .spouse-form-card__header,
    .spouse-found-card__header,
    .spouse-found-card__notice {
        align-items: flex-start;
        flex-direction: column;
        gap: 10px;
    }

    .spouse-search-card__header,
    .spouse-form-card__header {
        margin-bottom: 14px;
        padding-bottom: 14px;
    }

    .spouse-search-card h5,
    .spouse-form-card h5,
    .spouse-found-card h5,
    .spouse-empty-card h5 {
        font-size: 17px;
    }

    .spouse-input__control {
        font-size: 16px;
        height: 50px;
    }

    .spouse-form-card .row.gutters > [class*="col-"] {
        margin-bottom: 12px;
    }

    .spouse-form-card .form-group,
    .spouse-form-card .form-group.mb-0,
    .spouse-form-card .form-group.mb-lg-0 {
        margin-bottom: 0 !important;
    }

    .spouse-form-card label,
    .spouse-search-card label {
        font-size: 12px;
        margin-bottom: 6px;
    }

    .spouse-form-card .form-control,
    .spouse-form-card :deep(.select2-container--default .select2-selection--single),
    .phone-panel :deep(.vue-tel-input) {
        border-radius: 11px;
        min-height: 42px;
    }

    .spouse-form-card textarea.form-control {
        min-height: 86px;
    }

    .spouse-found-card__header {
        display: flex;
    }

    .spouse-found-card {
        border-radius: 15px;
        margin-top: 14px;
        padding: 14px;
    }

    .spouse-found-card__icon {
        height: 40px;
        width: 40px;
    }

    .spouse-found-card__grid {
        gap: 8px;
    }

    .spouse-found-card__field {
        border-radius: 12px;
        padding: 10px;
    }

    .spouse-found-card__field strong,
    .spouse-found-card__notice {
        font-size: 13px;
    }

    .spouse-found-card__badge {
        justify-self: start;
    }

    .spouse-search-card__actions,
    .spouse-search-card__actions .btn {
        width: 100%;
    }

    .phone-panel {
        border-radius: 15px;
        margin-top: 2px;
        padding: 14px 10px;
    }

    .phone-panel__header {
        margin-bottom: 12px;
    }

    .phone-panel__icon,
    .phone-list__icon {
        flex-basis: 40px;
        height: 40px;
        width: 40px;
    }

    .phone-panel__header strong {
        font-size: 14px;
    }

    .phone-panel__header small,
    .phone-list__content small {
        font-size: 12px;
    }

    .phone-list__item {
        align-items: flex-start;
        border-radius: 14px;
        gap: 10px;
        grid-template-columns: 40px minmax(0, 1fr) 36px;
        padding: 10px;
    }

    .phone-list__content strong {
        font-size: 13px;
        overflow-wrap: anywhere;
    }

    .phone-list__remove {
        height: 36px;
        width: 36px;
    }
}

@media (max-width: 991.98px) {
    html.dark-theme .spouse-hero {
        background: linear-gradient(160deg, #064e3b 0%, var(--dark-surface-2) 100%) !important;
    }
}

</style>
